/**
 * Parser for the MOHRE "List of Employees / كشف العاملين بالمنشأة" PDF, fed
 * with the text lines of every page (see pdfText.js). Pure: no pdf.js, no
 * Firestore.
 *
 * The PDF prints one value per line for each employee, in this order:
 * passport number, name (EN), name (AR), 14-digit person code, card type
 * (EN, may wrap over 2 lines), card type (AR), job (EN, may wrap), job (AR),
 * nationality (EN), nationality (AR), 9-digit card number, card expiry
 * dd/mm/yyyy, [card status note], contract type (EN), contract type (AR).
 *
 * Lines are classified by pattern, not by position: passport / person code /
 * card number / date are anchors that move the record from segment A (names)
 * to B (card type, job, nationality) to C (expiry, contract). Text lines are
 * assigned by script (Arabic vs Latin) and content within the segment, so a
 * missing line only loses its own field. A record split by the extractor
 * (e.g. its Arabic half printed after a page break, with a merged line such as
 * `نيبال18/07/2027محدد`) is re-joined and flagged `needsReview`.
 */

const ARABIC_RE = /\p{Script=Arabic}/u;
const ARABIC_RUN_RE = /\p{Script=Arabic}+/gu;
/** Starts with a letter, 6-12 letters/digits, at least 5 digits (A12345678, H8F401520, P8304018C, Q783775). */
const PASSPORT_RE = /^(?=(?:[A-Z0-9]*\d){5})[A-Z][A-Z0-9]{5,11}$/;
/** Passport and English name printed on one line ("H8F401520 SHARIFA ALI KHAMIS"): the extractor joins them when they share a baseline. */
const PASSPORT_NAME_RE = /^((?=(?:[A-Z0-9]*\d){5})[A-Z][A-Z0-9]{5,11})\s+([A-Z][A-Z .'-]+)$/;
const PERSON_CODE_RE = /^\d{14}$/;
const CARD_NUMBER_RE = /^\d{9}$/;
const DATE_RE = /(\d{2})\/(\d{2})\/(\d{4})/;
const DATE_ONLY_RE = /^\d{2}\/\d{2}\/\d{4}$/;
const DATETIME_RE = /\d{2}\/\d{2}\/\d{4}\s+\d{1,2}:\d{2}/;
const NUMBER_RE = /^\d+$/;
const CONTRACT_EN_RE = /^(un)?limited$/i;
const CONTRACT_AR_RE = /محدد/;
const CARD_TYPE_EN_RE = /PERMIT|WORK|CANCEL|VISA/;
const CARD_TYPE_AR_RE = /تصريح|بطاقة/;
/** A card type line that clearly continues on the next line ("... PERMIT UNDER" + "CANCELLATION"). */
const UNFINISHED_RE = /(UNDER|AND|OF|FOR|WITH|-|,)$/;
const HAS_LOWER_RE = /[a-z]/;

const TITLE_LINE_RE = /^(Passport Number|Person Name|Card Type|Job Name|Nationality|Card Number|Contract Type)$|رقم جواز|اسم الشخص|نوع البطاقة|ا?ملهنة|المهنة|^الجنسية$|رقم البطاقة|نوع العقد/;
const BODY_START_RE = /Contract Type/i;
const BODY_END_RE = /^Page\s+\d+\s*\/\s*\d+|Printing Date|عدد العمال/i;
const TOTAL_RE = /Total Number of Employees\s*:?\s*(\d+)/i;
const NOISE_RE = /^\*?RP\d+AE\*?$|^Establishment (Number|Name)|^Category\s*\/|Scan QR|^QR\b|List Of Employees|كشف العاملين/i;

const PRESENTATION_FORMS_RE = /[ﭐ-﷿ﹰ-﻿]/;
const ARABIC_RUN_WITH_SPACES_RE = /[\p{Script=Arabic}\s]+/gu;

/**
 * Some MOHRE PDFs carry their Arabic as presentation-form glyphs in visual
 * (right-to-left drawn) order: "دﺪﺤﻣ" for "محدد". Those lines are mapped back
 * to base letters (NFKC) and every Arabic run is reversed so the words read
 * logically and the Arabic patterns below match; lines without presentation
 * forms (already logical text) are left as they are.
 */
export const fixVisualArabic = (text) => {
    if (!PRESENTATION_FORMS_RE.test(text)) return text;
    // Intentional control character: strips NUL characters left in the extracted text.
    // eslint-disable-next-line no-control-regex
    const nfkc = text.normalize('NFKC').replace(/ /g, '');
    return nfkc.replace(ARABIC_RUN_WITH_SPACES_RE, run => Array.from(run).reverse().join(''));
};

const normalize = (line) => fixVisualArabic(String(line ?? '')).replace(/\s+/g, ' ').trim();

/** True when the lines carry the header of the MOHRE employee list. */
export const isMohreEmployeeList = (lines) => {
    const all = (lines || []).map(normalize);
    if (all.some(l => /List Of Employees|كشف العاملين/i.test(l))) return true;
    return all.some(l => /Passport Number/i.test(l)) && all.some(l => /Card Number/i.test(l)) && all.some(l => BODY_START_RE.test(l));
};

const newRecord = () => ({
    passportNumber: '', nameEn: '', nameAr: '', personCode: '',
    cardType: '', cardTypeAr: '', job: '', jobAr: '', nationality: '', nationalityAr: '',
    cardNumber: '', cardExpiry: null, contractType: '', contractTypeAr: '', cardStatus: '',
    // parser state (stripped before returning)
    _seg: 'A', _last: '', _extra: [], _split: false, _mergedDate: false
});

const append = (a, b) => (a ? `${a} ${b}` : b);

/** dd/mm/yyyy -> local Date (same construction as parseFlexibleDate, so ISO strings match the Excel path). */
const dateOf = (dd, mm, yyyy) => {
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return isNaN(d.getTime()) || d.getDate() !== Number(dd) ? null : d;
};

/**
 * Parse the text lines (all pages, in order) -> { employees, total, warnings }.
 * Each employee: { passportNumber, nameEn, nameAr, personCode, cardType,
 * cardTypeAr, job, jobAr, nationality, nationalityAr, cardNumber,
 * cardExpiry (Date|null), contractType, contractTypeAr, cardStatus,
 * needsReview, issues[] }.
 */
export const parseMohreEmployeeList = (lines) => {
    const warnings = [];
    const records = [];
    let cur = null;
    let inBody = false;
    let total = null;

    const label = (r) => r.passportNumber || r.nameEn || r.nameAr || r.cardNumber || `record ${records.length + 1}`;

    const start = (seed = []) => {
        if (cur) records.push(cur);
        cur = newRecord();
        seed.forEach(l => classify(l));
    };

    /** The record is closed (contract type seen) and a new anchor arrives: whatever came after the contract type belongs to a new record. */
    const restart = () => { const seed = cur._extra; cur._extra = []; start(seed); };

    /** An anchor arrives for a record that already reached a later segment: it was split by the extractor. Names left in `_extra` are recovered. */
    const reopen = (seg) => {
        cur._split = true;
        const keep = [];
        cur._extra.forEach(l => {
            if (ARABIC_RE.test(l) && !cur.nameAr) cur.nameAr = l;
            else if (!ARABIC_RE.test(l) && !cur.nameEn && !CARD_TYPE_EN_RE.test(l)) cur.nameEn = l;
            else keep.push(l);
        });
        cur._extra = keep;
        cur._seg = seg;
    };

    const closeContract = () => {
        // Lines between the expiry and the contract type are a card status note ("Cards being cancelled ...")
        const note = cur._extra.map(l => l.replace(ARABIC_RUN_RE, '').replace(/\s+/g, ' ').trim()).filter(Boolean).join(' ');
        if (note && !cur.cardStatus) cur.cardStatus = note;
        cur._extra = [];
    };

    const setExpiry = (m, merged) => {
        const d = dateOf(m[1], m[2], m[3]);
        if (!d) { warnings.push(`Unreadable date "${m[0]}" for ${label(cur)}.`); return; }
        cur.cardExpiry = d;
        if (merged) cur._mergedDate = true;
        cur._seg = 'C';
    };

    const classifyLatin = (line) => {
        if (cur._seg === 'A') {
            if (CARD_TYPE_EN_RE.test(line)) cur._seg = 'B';
            else if (!cur.nameEn) { cur.nameEn = line; cur._last = 'nameEn'; return; }
            else if (!cur.nameAr && !HAS_LOWER_RE.test(line)) { cur.nameEn = append(cur.nameEn, line); return; } // long name wrapped
            else cur._seg = 'B';
        }
        if (cur._seg === 'B') {
            const caps = !HAS_LOWER_RE.test(line);
            if (CARD_TYPE_EN_RE.test(line) && caps) { cur.cardType = append(cur.cardType, line); cur._last = 'cardType'; return; }
            if (caps && cur._last === 'cardType' && UNFINISHED_RE.test(cur.cardType)) { cur.cardType = append(cur.cardType, line); return; }
            if (!caps) { cur.job = append(cur.job, line); cur._last = 'job'; return; }
            cur.nationality = append(cur.nationality, line); cur._last = 'nationality'; return;
        }
        cur._extra.push(line); cur._last = 'extra';
    };

    const classifyArabic = (line) => {
        if (cur._seg === 'A') {
            if (!cur.nameAr && !CARD_TYPE_AR_RE.test(line)) { cur.nameAr = line; cur._last = 'nameAr'; return; }
            cur._seg = 'B';
        }
        if (cur._seg === 'B') {
            if (!cur.cardTypeAr && CARD_TYPE_AR_RE.test(line)) { cur.cardTypeAr = line; cur._last = 'cardTypeAr'; return; }
            if (cur._last === 'cardTypeAr' && CARD_TYPE_AR_RE.test(line) && !cur.jobAr) { cur.cardTypeAr = append(cur.cardTypeAr, line); return; }
            if (!cur.jobAr) { cur.jobAr = line; cur._last = 'jobAr'; return; }
            if (!cur.nationalityAr) { cur.nationalityAr = line; cur._last = 'nationalityAr'; return; }
        }
        if (cur._seg === 'C' && CONTRACT_AR_RE.test(line)) {
            if (cur.contractTypeAr) { restart(); classify(line); return; }
            cur.contractTypeAr = line; cur._last = 'contractTypeAr'; closeContract(); return;
        }
        cur._extra.push(line); cur._last = 'extra';
    };

    const classify = (line) => {
        if (PERSON_CODE_RE.test(line)) {
            if (cur.personCode) { restart(); cur.personCode = line; cur._seg = 'B'; return; }
            if (cur._seg !== 'A') reopen('B'); else cur._seg = 'B';
            cur.personCode = line; cur._last = 'personCode'; return;
        }
        if (CARD_NUMBER_RE.test(line)) {
            if (cur.cardNumber) { restart(); cur.cardNumber = line; cur._seg = 'C'; return; }
            if (cur.contractType || cur.contractTypeAr) reopen('C');
            cur.cardNumber = line; cur._seg = 'C'; cur._last = 'cardNumber'; return;
        }
        const exact = DATE_ONLY_RE.test(line);
        const dm = line.match(DATE_RE);
        if (dm) {
            if (cur.cardExpiry) { restart(); classify(line); return; }
            const before = line.slice(0, dm.index).trim();
            const after = line.slice(dm.index + dm[0].length).trim();
            if (before) classify(before); // still in the current segment (e.g. the Arabic nationality glued to the date)
            if (cur.contractType || cur.contractTypeAr) reopen('C');
            setExpiry(dm, !exact);
            if (after) classify(after);
            return;
        }
        if (NUMBER_RE.test(line)) { warnings.push(`Ignored number "${line}" near ${label(cur)}.`); return; }
        if (CONTRACT_EN_RE.test(line)) {
            if (cur.contractType) { restart(); cur.contractType = line; cur._seg = 'C'; return; }
            cur.contractType = line; cur._seg = 'C'; cur._last = 'contractType'; closeContract(); return;
        }
        if (ARABIC_RE.test(line)) classifyArabic(line);
        else classifyLatin(line);
    };

    (lines || []).forEach(raw => {
        const line = normalize(raw);
        if (!line) return;
        const tm = line.match(TOTAL_RE);
        if (tm) { total = Number(tm[1]); inBody = false; return; }
        if (BODY_END_RE.test(line)) { inBody = false; return; }
        if (!inBody) { if (BODY_START_RE.test(line)) inBody = true; return; }
        if (TITLE_LINE_RE.test(line) || NOISE_RE.test(line) || DATETIME_RE.test(line)) return;
        const pm = PASSPORT_RE.test(line) ? [line, line, ''] : line.match(PASSPORT_NAME_RE);
        if (pm) {
            if (cur && cur._extra.length > 0 && (cur.contractType || cur.contractTypeAr)) {
                warnings.push(`Ignored ${cur._extra.length} unassigned line(s) after ${label(cur)}: ${cur._extra.join(' | ')}`);
                cur._extra = [];
            }
            start();
            cur.passportNumber = pm[1];
            if (pm[2]) classify(pm[2].trim()); // the name that shared the passport's line
            return;
        }
        if (!cur) {
            if (NUMBER_RE.test(line)) { warnings.push(`Ignored number "${line}" before the first employee.`); return; }
            if (!/[A-Za-z0-9]/.test(line)) { warnings.push(`Ignored "${line}" before the first employee.`); return; } // a stray Arabic title
            start(); // a record whose passport line is missing
        }
        classify(line);
    });
    if (cur) records.push(cur);

    const employees = records.map(r => {
        const issues = [];
        if (!r.passportNumber) issues.push('no passport number');
        if (!r.nameEn) issues.push(r.nameAr ? 'no English name' : 'no name');
        if (!r.cardNumber) issues.push('no card number');
        if (!r.cardExpiry) issues.push('no card expiry');
        if (r._split) issues.push('record was split across lines/pages and re-joined');
        if (r._mergedDate) issues.push('expiry date read from a merged line');
        if (r._extra.length > 0) issues.push(`unassigned text: ${r._extra.join(' | ')}`);
        const { _seg, _last, _extra, _split, _mergedDate, ...employee } = r;
        return { ...employee, needsReview: issues.length > 0, issues };
    });

    const seen = new Set();
    employees.forEach(e => {
        if (!e.passportNumber) return;
        if (seen.has(e.passportNumber)) warnings.push(`Passport ${e.passportNumber} appears more than once.`);
        seen.add(e.passportNumber);
    });
    if (total === null) warnings.push('"Total Number of Employees" not found in the PDF.');
    else if (total !== employees.length) warnings.push(`The PDF says ${total} employee(s) but ${employees.length} were parsed.`);

    return { employees, total, warnings };
};

export default parseMohreEmployeeList;
